import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Formik, FormikHelpers } from 'formik'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router'
import { Stack, Heading, useResponsiveValue, useDialogControllers } from '@machobear/design-system'
import { headKey } from '@/utils'

import PropertyTypeSection from './property-type-section'
import LocationSection from './location-section'
import ListingExistsModal from './listing-exists-modal'
import ListingRelistModal from './listing-relist-modal'
import { CreateFormValues, Listing } from '../types'
import { createValidationSchema } from '../util/vallidationSchema'
import FormFooter from '../components/footer'
import FormWrapper from '../form-wrapper'
import useRedirectPublishedListing from '../hooks/use-redirect-published-listing'

const CreateListingForm = ({ data, isLoading }: { data: Listing; isLoading: boolean }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  useRedirectPublishedListing(data)

  const [existingListingId, setExistingListingId] = useState<string | null>(null)
  const { visible, showModal, closeModal } = useDialogControllers({
    listingExists: false,
    listingRelist: false
  })

  const initialValues: CreateFormValues = {
    type: data?.unit_type_txt_id || '',
    scope: data?.unit_type_scope_txt_id || '',
    groupListing: data?.isGroupListing ? '1' : '0',
    building: data?.building || '',
    building_id: data?.building_id || null,
    full_address: {
      text: data?.building?.name || '',
      ...(data?.building_id ? { value: data?.building } : {}),
      isOptional: false
    },
    address: data?.building?.full_address || '',
    suite: data?.unit_number || '',
    building_name: data?.building?.name || '',
    hide_unit_number: !!data?.hide_unit_number,
    street_number: data?.building?.street_number || '',
    city: data?.building?.city || '',
    province: data?.building?.state || '',
    zip: data?.building?.zip || '',
    country: 'ca'
  }

  const onSubmit = async (
    values: CreateFormValues,
    { setSubmitting }: FormikHelpers<CreateFormValues>
  ) => {
    const streetAddressParts = {
      street_name: values.building?.street_name || values.address.replace(/^\s*([0-9]+)/, ''),
      street_number: values.street_number || values.address.match(/^\s*([0-9]+)/)?.[1] || ''
    }

    if (!data?.listing_id || data.unit_number !== values.suite) {
      const { error: validationError, body: validationBody } =
        await dispatch.listing.verifyCreateListing({
          unit_number: values.suite,
          street_number: streetAddressParts.street_number,
          zip: values.zip
        })

      if (validationError) return setSubmitting(false)

      setExistingListingId(validationBody.listing_id)

      if (validationBody.listing_allowed_to_clone) {
        showModal('listingRelist')()
        return setSubmitting(false)
      }
      if (!validationBody.available_to_list) {
        showModal('listingExists')()
        return setSubmitting(false)
      }
    }
    if (!values.building_id) {
      const { body, error } = await dispatch.building.create({
        ...values,
        address: streetAddressParts.street_name,
        street_number: streetAddressParts.street_number
      })
      if (error) return setSubmitting(false)
      values = { ...values, building_id: body.id }
    }
    const response = data?.listing_id
      ? await dispatch.listing.updateUnit({
          from_building: data.building_id,
          building_id: values.building_id,
          hide_unit_number: values.hide_unit_number,
          unit_number: values.suite,
          unit_id: data.unit_id,
          listing_id: data.listing_id
        })
      : await dispatch.listing.create({
          ...values,
          address: streetAddressParts.street_name,
          street_number: streetAddressParts.street_number
        })
    if (response.response.ok) {
      const listingId = headKey(response.body.listings)
      history.replace(`/listing/create/${listingId}/details`)
    } else {
      setSubmitting(false)
    }
  }

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={createValidationSchema}
      enableReinitialize
    >
      {({ values, isSubmitting }) => {
        const isApartment = values.type === 'condo'

        return (
          <>
            <ListingRelistModal
              visible={visible.listingRelist}
              closeModal={closeModal('listingRelist')}
              listingId={existingListingId}
              values={values}
            />
            <ListingExistsModal
              visible={visible.listingExists}
              closeModal={closeModal('listingExists')}
              listingId={existingListingId}
            />
            <FormWrapper>
              <Form>
                <Stack align="left" space={{ mobile: 'gutter', desktop: 'large' }}>
                  {!isMobile && (
                    <Heading level="4" component="h2">
                      {t('Create Listing', 'Create Listing')}
                    </Heading>
                  )}
                  <PropertyTypeSection isLoading={isLoading} isApartment={isApartment} />
                  <LocationSection isLoading={isLoading} isApartment={isApartment} />
                  <FormFooter
                    isLoading={isLoading}
                    isSubmitting={isSubmitting}
                    step="create-listing"
                  />
                </Stack>
              </Form>
            </FormWrapper>
          </>
        )
      }}
    </Formik>
  )
}

export default CreateListingForm
